import { environment } from '@env/environment';
import { LinkInterface } from '@shared/interfaces/attribute-description.interface';
import { ATTRIBUTES_TO_REMOVE } from '../constants/common';
import { EntityName } from '../enums/entity-name.enum';
import { IconType } from '../enums/internal-icon.enum';
import { TokenTypes } from '../enums/token-type';

export class StringUtils {
  public static includes(
    source: string,
    key: string,
    caseSensitive = false
  ): boolean {
    if (!source || !key) {
      return false;
    }

    if (caseSensitive) {
      return source.includes(key);
    }

    return source.toLowerCase().includes(key.toLowerCase());
  }
  /**
   *
   * @param str str1
   * @param str2 str2
   * @param caseSensitive default = false
   */
  public static equal(
    str: string,
    str2: string,
    caseSensitive = false
  ): boolean {
    if (!str || !str2) {
      return false;
    }

    if (caseSensitive) {
      return str === str2;
    }

    return str.toLowerCase() === str2.toLowerCase();
  }

  public static toLowerCaseFirstLetter(str: string): string {
    if (str) {
      return str.charAt(0).toLowerCase() + str.slice(1);
    }
  }

  public static toUpperCaseFirstLetter(str: string): string {
    if (str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    }
  }

  /**
   * Check if content is empty
   * Empty html tag is consider as empty
   */
  public static isEmptyHtmlContent(content: string): boolean {
    const isEmptyString = !content || content.trim() === '';
    if (isEmptyString) {
      return true;
    }

    const tmp = document.createElement('div');
    tmp.innerHTML = content;
    const pureContent = tmp.textContent || tmp.innerText || '';
    return !this.containMediaTags(content) && pureContent.trim() === '';
  }

  public static containMediaTags(content: string): boolean {
    const mediaTags = [
      '<img',
      '<video',
      '<audio',
      '<iframe',
      '<embed',
      '<picture',
    ];

    return mediaTags.some((type: string) => content.includes(type));
  }

  public static replacePathLastSegment(
    segment: string,
    currentUrlReplaced = true
  ): void {
    const url = new URL(globalThis.location.href);
    const segments = url.pathname.split('/').filter(Boolean);

    if (!segments.length) return;

    segments[segments.length - 1] = segment;
    url.pathname = `/${segments.join('/')}`;

    if (url.toString() !== globalThis.location.href) {
      if (currentUrlReplaced) {
        this.replaceCurrentUrl(url);
      } else {
        this.pushCurrentUrl(url);
      }
    }
  }

  public static getParamFromUrl(key: string, sourceUrl = null): string {
    const url = new URL(this.resolveSourceUrl(sourceUrl));
    return url.searchParams.get(key);
  }

  /**
   * Copies a query param of the current page onto a target url, so in-app
   * navigation carries it along. Leaves the url untouched when the param is
   * absent on the current page, already present on the target, or the
   * target is not a same-origin http(s) page.
   */
  public static propagateParamFromCurrentUrl(url: string, key: string): string {
    if (!url) {
      return url;
    }

    const value = this.getParamFromUrl(key);

    if (!value) {
      return url;
    }

    try {
      const target = new URL(url, globalThis.location.href);
      const httpProtocol =
        target.protocol === 'http:' || target.protocol === 'https:';

      if (
        !httpProtocol ||
        target.origin !== globalThis.location.origin ||
        target.searchParams.has(key)
      ) {
        return url;
      }

      target.searchParams.set(key, value);

      return target.toString();
    } catch {
      return url;
    }
  }

  public static setQueryParamOnUrl(
    key: string,
    value: string,
    sourceUrl = null,
    currentUrlReplaced = false
  ): void {
    const url = new URL(this.resolveSourceUrl(sourceUrl));
    url.searchParams.set(key, value);

    // Only update the URL if it's different from the current one
    if (url.toString() !== globalThis.location.href) {
      if (currentUrlReplaced) {
        this.replaceCurrentUrl(url);
      } else {
        this.pushCurrentUrl(url);
      }
    }
  }

  public static removeParamFromUrl(
    key: string,
    sourceUrl = null,
    currentUrlReplaced = false
  ): void {
    const url = new URL(this.resolveSourceUrl(sourceUrl));
    url.searchParams.delete(key);

    if (url.toString() !== globalThis.location.href) {
      if (currentUrlReplaced) {
        this.replaceCurrentUrl(url);
      } else {
        this.pushCurrentUrl(url);
      }
    }
  }

  public static removeParamByKey(sourceUrl: string, key: string): string {
    const regex = new RegExp(`([?&])${key}=[^&]*(&)?`, 'gi');
    return sourceUrl.replace(regex, (match, p1, p2) => (p2 ? p1 : ''));
  }

  public static removeAllParamsFromUrl(srcUrl: string): string {
    return srcUrl?.split('?')[0];
  }

  private static replaceCurrentUrl(url: URL): void {
    globalThis.history.replaceState(null, null, url);
  }

  private static pushCurrentUrl(url: URL): void {
    globalThis.history.pushState(null, null, url);
  }

  // An empty source url (e.g. `href` of an anchor without one) must fall back
  // to the current page too, so a nullish check alone is not enough.
  private static resolveSourceUrl(sourceUrl: string): string {
    return sourceUrl?.length ? sourceUrl : globalThis.location.href;
  }

  public static getEntityUrlByEntityName(entityName: string): string {
    let entityUrl = '';
    switch (entityName) {
      case EntityName.Venture:
        entityUrl = environment.jipUrl.venture;
        break;

      case EntityName.Challenge:
        entityUrl = environment.jipUrl.challenges;
        break;

      case EntityName.Organization:
        entityUrl = environment.jipUrl.organizations;
        break;

      case EntityName.Person:
        entityUrl = environment.jipUrl.people;
        break;

      case EntityName.Event:
        entityUrl = environment.jipUrl.event;
        break;

      case EntityName.Post:
        entityUrl = environment.jipUrl.post;
        break;

      case EntityName.News:
        entityUrl = environment.jipUrl.news;
        break;

      case EntityName.Group:
        entityUrl = environment.jipUrl.groups;
        break;
    }
    return entityUrl;
  }

  public static getEntityUrlByTokenTypes(tokenTypes: string): string {
    let entityUrl = '';
    switch (tokenTypes) {
      case TokenTypes.InviteVenture:
        entityUrl = environment.jipUrl.venture;
        break;

      case TokenTypes.InvitePeople:
        entityUrl = environment.jipUrl.people;
        break;

      case TokenTypes.InviteOrganization:
        entityUrl = environment.jipUrl.organizations;
        break;

      case TokenTypes.InviteChallenge:
      case TokenTypes.InviteChallengeJuror:
        entityUrl = environment.jipUrl.challenges;
        break;

      case TokenTypes.InviteEvent:
        entityUrl = environment.jipUrl.event;
        break;

      case TokenTypes.InviteGroup:
        entityUrl = environment.jipUrl.groups;
        break;
    }
    return entityUrl;
  }

  public static stripHtml(sanitizeHtml: string): string {
    const temp = document.createElement('DIV');
    temp.innerHTML = sanitizeHtml;
    return temp.textContent || temp.innerText || '';
  }

  public static stripAttributesFromHtmlString(
    sanitizeHtml: string,
    attributesToRemove: string[] = ATTRIBUTES_TO_REMOVE
  ): string {
    if (attributesToRemove?.length > 0) {
      const temp = document.createElement('DIV');
      temp.innerHTML = sanitizeHtml;
      const allChilds = temp.getElementsByTagName('*');

      for (const child of Array.from(allChilds)) {
        for (const attr of attributesToRemove) {
          child.removeAttribute(attr);
        }
      }
      return temp.innerHTML;
    }
    return sanitizeHtml;
  }

  public static replaceTagFromHtmlString(
    sanitizeHtml: string,
    tags: string[],
    replaceTag: string
  ): string {
    const temp = document.createElement('DIV');
    temp.innerHTML = sanitizeHtml;

    if (tags?.length > 0) {
      for (const tag of tags) {
        const allTags = temp.getElementsByTagName(tag);

        for (const tagEl of Array.from(allTags)) {
          const replaceTagEl = document.createElement(replaceTag);
          replaceTagEl.innerHTML = tagEl.innerHTML;
          tagEl.replaceWith(replaceTagEl);
        }
      }
    }

    return temp.innerHTML;
  }

  public static replaceAll(
    text: string,
    find: string,
    replace: string
  ): string {
    return text.replaceAll(new RegExp(find, 'g'), replace);
  }

  public static getIconByType(
    name: string,
    iconType: IconType,
    theme = ''
  ): string {
    const themePath = theme ? `-${theme}` : '';

    return name && iconType
      ? `${environment.wordpressJipImages}icons/${name}${themePath}.${iconType}`
      : '';
  }

  public static isRTEContentMaxlengthError(
    text: string,
    maxlength: number
  ): boolean {
    return maxlength > 0 && text?.length > maxlength;
  }

  public static removeLineBreakFromTextArea(sanitizeHtml: string): string {
    return sanitizeHtml ? sanitizeHtml.replaceAll(/(\r\n|\n|\r)/gm, '') : '';
  }

  public static getLocaleFromUrl(): string | null {
    const paths = location.pathname.split('/');

    return paths[1] || null; // first sub path
  }

  /** Checks whether a value is a locale segment, e.g. "en_us" or "de-CH". */
  public static isLocale(value: string): boolean {
    return /^[a-z]{2}[_-][a-z]{2}$/i.test(value ?? '');
  }

  /**
   * Checks whether the current URL starts with a locale segment
   * (e.g. /en_us/my-profile). getLocaleFromUrl() alone is not enough: it
   * returns the first path segment verbatim, locale or not.
   */
  public static hasLocaleInUrl(): boolean {
    return StringUtils.isLocale(StringUtils.getLocaleFromUrl());
  }

  public static isString(content: unknown): boolean {
    return typeof content === 'string';
  }

  public static getCorrectUrl(unknownUrlSrc: string & LinkInterface): string {
    if (unknownUrlSrc?.url === undefined) {
      return unknownUrlSrc;
    }
    if (unknownUrlSrc.url === null) {
      return '';
    }
    return unknownUrlSrc.url;
  }

  public static generateLinkedinLoginLink(
    pathName: string,
    clientId: string,
    redirectUri: string,
    state: string
  ): string {
    return `${pathName}/?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&state=${state}&scope=r_liteprofile%20r_emailaddress`;
  }

  public static extractContent(html: string): string {
    return new DOMParser().parseFromString(html, 'text/html').documentElement
      .textContent;
  }

  public static prefixUrl(urlValue: string): string {
    if (!urlValue) return '';
    return urlValue.includes('http') ? urlValue : `http://${urlValue}`;
  }

  public static buildEntityWithLinkText(linkText: string, url: string): string {
    return url ? `${linkText} (${url})` : linkText;
  }

  public static getEntityNameByUrl(url: string): string | EntityName {
    const { jipUrl } = environment;

    if (url.includes(jipUrl.organizations)) {
      return EntityName.Organization;
    }

    if (url.includes(jipUrl.challenges)) {
      return EntityName.Challenge;
    }

    if (url.includes(jipUrl.venture)) {
      return EntityName.Venture;
    }

    if (url.includes(jipUrl.event)) {
      return EntityName.Event;
    }

    if (url.includes(jipUrl.people)) {
      return EntityName.Person;
    }

    return EntityName.All;
  }

  public static convertTextareaToTinyMCE(text: string): string {
    if (!text?.trim() || text.includes('<p>')) {
      return text;
    }

    const lines = text.split(/\r\n|\r|\n/);
    const paragraphs: string[] = [];
    let currentBlock: string[] = [];

    for (const element of lines) {
      const line = element;
      const trimmed = line.trim();

      if (trimmed === '') {
        if (currentBlock.length > 0) {
          paragraphs.push(currentBlock.join('<br>'));
          currentBlock = [];
        }
        continue;
      }

      currentBlock.push(line || '&nbsp;');
    }

    if (currentBlock.length > 0) {
      paragraphs.push(currentBlock.join('<br>'));
    }

    return paragraphs.map(block => `<p>${block}</p>`).join('');
  }
}
